import { createHexToDecimalOrId } from '../../../../../helpers/utilities';
import type { FlowNode } from '../../../types/flow-types';
import type { SceneJsonContext } from '../../../nodes/definitions/types';
import type { FlowCardDefinition } from '../types';
import { virtualComponentRuntimeProperty } from '../virtual-components';

const comparisonOptions = {
	number: [
		{ value: 'eq', label: 'Equals' },
		{ value: 'ne', label: 'Does not equal' },
		{ value: 'gt', label: 'Is above' },
		{ value: 'lt', label: 'Is below' },
		{ value: 'ge', label: 'Is at least' },
		{ value: 'le', label: 'Is at most' },
	],
	boolean: [
		{ value: 'eq', label: 'Is' },
		{ value: 'ne', label: 'Is not' },
	],
	enum: [
		{ value: 'eq', label: 'Is' },
		{ value: 'ne', label: 'Is not' },
	],
	text: [
		{ value: 'eq', label: 'Equals' },
		{ value: 'ne', label: 'Does not equal' },
	],
};

const buttonEvents = [
	{ value: 'S', label: 'Single press' },
	{ value: 'SS', label: 'Double press' },
	{ value: 'SSS', label: 'Triple press' },
	{ value: 'L', label: 'Long press' },
];

function componentName(data: Record<string, any>): string {
	return String(data.component?.name || '').trim() || 'virtual component';
}

function backendId(component: Record<string, any>): string | number {
	const raw = String(component?.parentDeviceId || '');
	return raw.toLowerCase().startsWith('x') ? raw : createHexToDecimalOrId(raw);
}

function componentRef(component: Record<string, any>): string {
	return `dev:${backendId(component)}:${virtualComponentRuntimeProperty(component)}`;
}

function metadata(node: FlowNode, paletteKey: string) {
	return {
		_fid: node.id,
		_flow_card: paletteKey,
		_flow_card_version: '1.0.0',
		_flow_virtual_component: node.data.component,
	};
}

function normalizedValue(data: Record<string, any>): unknown {
	const type = data.component?.type;
	if (type === 'number') return Number(data.value ?? 0);
	return String(data.value ?? '');
}

function compileCondition(
	node: FlowNode,
	context: SceneJsonContext | undefined,
	value: unknown,
	paletteKey: string,
): Record<string, unknown> | null {
	const component = node.data.component as Record<string, any> | undefined;
	if (!component?.parentDeviceId || !component.type) return null;
	const ref = componentRef(component);
	const operator = String(node.data.operator || 'eq');
	return {
		...(context?.nested
			? { r: ref, cmpop: operator, cmpval: value }
			: { [operator]: [{ rr: ref }, value] }),
		_gui_purpose: 'condition',
		...metadata(node, paletteKey),
	};
}

function migrateComponent(data: Record<string, unknown>): Record<string, any> | undefined {
	if (data.component && typeof data.component === 'object') {
		const component = data.component as Record<string, any>;
		return {
			...component,
			ref:
				component.ref ||
				`${component.parentDeviceId || ''}|${component.componentKey || `${component.type}:${component.index}`}`,
		};
	}
	const sceneData = data.sceneData as any;
	return sceneData?._flow_virtual_component as Record<string, any> | undefined;
}

export const virtualComponentEventCard: FlowCardDefinition = {
	type: 'virtualComponentEvent',
	version: '1.0.0',
	domain: 'core',
	role: 'condition',
	category: 'conditions',
	label: 'Virtual Button Event',
	description: 'Start a branch when an existing virtual button is pressed',
	icon: 'fa-circle-dot',
	color: '#0ea5e9',
	deviceRequirement: 'virtual-component',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['component', 'event'],
		properties: {
			component: {
				type: 'object',
				properties: { type: { type: 'string', enum: ['button'] } },
			},
			event: { type: 'string', enum: buttonEvents.map((option) => option.value) },
			operator: { type: 'string', enum: ['eq'] },
		},
	},
	fields: [
		{
			path: 'component',
			label: 'Virtual button',
			type: 'virtual-component',
			virtualComponent: { types: ['button'], access: 'read' },
		},
		{ path: 'event', label: 'Button event', type: 'select', options: buttonEvents },
	],
	ports: { sources: ['true'] },
	defaults: () => ({ component: undefined, event: 'S', operator: 'eq', cardVersion: '1.0.0' }),
	summary: (data) => {
		if (!data.component?.ref) return 'Choose a virtual button';
		const event = buttonEvents.find((option) => option.value === data.event)?.label || 'Press';
		return `${event} on ${componentName(data)}`;
	},
	validate: (data) =>
		data.component?.ref ? [] : [{ path: 'component', message: 'Select a virtual button' }],
	compile: (node, context) =>
		compileCondition(node, context, String(node.data.event || 'S'), 'virtualComponentEvent'),
	migrate: (data) => ({
		...data,
		component: migrateComponent(data),
		event: data.event ?? data.value ?? 'S',
		operator: 'eq',
	}),
};

export const virtualComponentConditionCard: FlowCardDefinition = {
	type: 'virtualComponentCondition',
	version: '1.0.0',
	domain: 'core',
	role: 'condition',
	category: 'conditions',
	label: 'Virtual Value Condition',
	description: 'Compare the current value of a virtual number, boolean, enum, or text component',
	icon: 'fa-code-compare',
	color: '#06b6d4',
	deviceRequirement: 'virtual-component',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['component', 'operator'],
		properties: {
			component: {
				type: 'object',
				properties: {
					type: { type: 'string', enum: ['number', 'boolean', 'enum', 'text'] },
				},
			},
			operator: {
				type: 'string',
				enum: [
					...new Set(
						Object.values(comparisonOptions)
							.flat()
							.map((option) => option.value),
					),
				],
			},
			value: { type: 'string' },
		},
	},
	fields: [
		{
			path: 'component',
			label: 'Virtual component',
			type: 'virtual-component',
			virtualComponent: { types: ['number', 'boolean', 'enum', 'text'], access: 'read' },
		},
		{
			path: 'operator',
			label: 'Comparison',
			type: 'select',
			optionsFrom: { path: 'component.type', cases: comparisonOptions },
		},
		{ path: 'value', label: 'Value', type: 'component-value' },
	],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({ component: undefined, operator: 'eq', value: '', cardVersion: '1.0.0' }),
	summary: (data) => {
		if (!data.component?.ref) return 'Choose a virtual component';
		const symbols: Record<string, string> = { eq: '=', ne: '≠', gt: '>', lt: '<', ge: '≥', le: '≤' };
		return `${componentName(data)} ${symbols[data.operator] || data.operator || '='} ${String(data.value ?? '')}${data.component?.unit || ''}`;
	},
	normalize: (data) => {
		const allowed = comparisonOptions[data.component?.type as keyof typeof comparisonOptions] || [];
		const operator = allowed.some((option) => option.value === data.operator)
			? data.operator
			: allowed[0]?.value || 'eq';
		return { ...data, operator };
	},
	validate: (data) => {
		if (!data.component?.ref) return [{ path: 'component', message: 'Select a virtual component' }];
		if (data.value === '' || data.value == null)
			return [{ path: 'value', message: 'Set a comparison value' }];
		return [];
	},
	compile: (node, context) =>
		compileCondition(
			node,
			context,
			(node.data.component as Record<string, any> | undefined)?.type === 'number'
				? Number(node.data.value)
				: String(node.data.value ?? ''),
			'virtualComponentCondition',
		),
	migrate: (data) => ({
		...data,
		component: migrateComponent(data),
		operator: data.operator ?? 'eq',
		value: data.value ?? '',
	}),
};

function virtualActionCard(emitButton: boolean): FlowCardDefinition {
	const paletteKey = emitButton ? 'emitVirtualButtonEvent' : undefined;
	return {
		type: 'virtualComponentAction',
		...(paletteKey ? { paletteKey } : {}),
		version: '1.0.0',
		domain: 'core',
		role: 'action',
		category: 'actions',
		label: emitButton ? 'Emit Virtual Button Event' : 'Set Virtual Component',
		description: emitButton
			? 'Programmatically emit a press event from an existing virtual button'
			: 'Write a value to an existing virtual number, boolean, enum, or text component',
		icon: emitButton ? 'fa-hand-pointer' : 'fa-sliders',
		color: emitButton ? '#f59e0b' : '#8b5cf6',
		deviceRequirement: 'virtual-component',
		schema: {
			$schema: 'https://json-schema.org/draft/2020-12/schema',
			type: 'object',
			required: ['component', emitButton ? 'event' : 'value'],
			properties: {
				component: {
					type: 'object',
					properties: {
						type: {
							type: 'string',
							enum: emitButton ? ['button'] : ['number', 'boolean', 'enum', 'text'],
						},
					},
				},
				event: { type: 'string', enum: buttonEvents.map((option) => option.value) },
				value: { type: 'string' },
				delay: {
					type: 'object',
					properties: {
						value: { type: 'number', minimum: 0 },
						units: { type: 'string', enum: ['sec', 'min'] },
					},
				},
			},
		},
		fields: [
			{
				path: 'component',
				label: emitButton ? 'Virtual button' : 'Virtual component',
				type: 'virtual-component',
				virtualComponent: {
					types: emitButton ? ['button'] : ['number', 'boolean', 'enum', 'text'],
					access: 'write',
				},
			},
			...(emitButton
				? [
						{
							path: 'event',
							label: 'Button event',
							type: 'select',
							options: buttonEvents,
						} as const,
					]
				: [{ path: 'value', label: 'New value', type: 'component-value' } as const]),
			{ path: 'delay.value', label: 'Delay', type: 'number', min: 0, step: 1 },
			{
				path: 'delay.units',
				label: 'Delay unit',
				type: 'select',
				options: [
					{ value: 'sec', label: 'Seconds' },
					{ value: 'min', label: 'Minutes' },
				],
			},
		],
		ports: { sources: ['output'] },
		defaults: () => ({
			component: undefined,
			...(emitButton ? { event: 'S' } : { value: '' }),
			delay: { value: 0, units: 'sec' },
			cardVersion: '1.0.0',
		}),
		summary: (data) => {
			if (!data.component?.ref) return `Choose a virtual ${emitButton ? 'button' : 'component'}`;
			if (emitButton) {
				const event =
					buttonEvents.find((option) => option.value === data.event)?.label || 'Press';
				return `Emit ${event.toLowerCase()} on ${componentName(data)}`;
			}
			return `Set ${componentName(data)} to ${String(data.value ?? '')}${data.component?.unit || ''}`;
		},
		validate: (data) => {
			if (!data.component?.ref)
				return [
					{
						path: 'component',
						message: `Select a virtual ${emitButton ? 'button' : 'component'}`,
					},
				];
			if (!emitButton && (data.value === '' || data.value == null))
				return [{ path: 'value', message: 'Set a value' }];
			return [];
		},
		compile: (node) => {
			const component = node.data.component as Record<string, any> | undefined;
			if (!component?.parentDeviceId) return null;
			const value = emitButton ? String(node.data.event || 'S') : normalizedValue(node.data);
			return {
				r: componentRef(component),
				set: value,
				_gui_device_id: String(component.parentDeviceId),
				_gui_purpose: 'action',
				...metadata(node, emitButton ? 'emitVirtualButtonEvent' : 'virtualComponentAction'),
			};
		},
		migrate: (data) => ({
			...data,
			component: migrateComponent(data),
			...(emitButton
				? { event: data.event ?? data.value ?? (data.sceneData as any)?.set ?? 'S' }
				: { value: data.value ?? (data.sceneData as any)?.set ?? '' }),
			delay: data.delay ?? { value: 0, units: 'sec' },
		}),
	};
}

export const virtualComponentActionCards = [virtualActionCard(false), virtualActionCard(true)];
